/* eslint-disable no-param-reassign */
import getHostUrl from '../getWsHostUrl';
import { Observable } from 'lib0/observable';
import * as time from 'lib0/time';
import * as math from 'lib0/math';

import {
	messageTypeMaintenance,
	messageTypeServer,
	messageTypeBroadcast,
	subscribeEvent,
	unsubscribeEvent,
	errorEvent,
} from './constants';

const setupWS = (provider) => {
	if (!provider.shouldConnect || provider.ws != null) return;

	const websocket = new WebSocket(provider.url);
	websocket.binaryType = 'arraybuffer';

	provider.ws = websocket;
	provider.connecting = true;
	provider.connected = false;

	websocket.onmessage = (event) => {
		provider.lastMessageReceived = time.getUnixTime();

		const { data } = event;
		const dataObj = JSON.parse(data);
		if (dataObj.messageType === messageTypeMaintenance && dataObj.event === errorEvent) {
			websocket.onerror(dataObj.data.error);
		} else {
			provider.emit(dataObj.room, [dataObj.data]);
		}
	};

	websocket.onerror = (event) => {
		console.error('GeneralWebsocket error', event);
		provider.emit('error', ['Websocket Error', event, provider]);
		provider.emit('status', [{ status: 'error', error: 'Websocket Error' }]);
	};

	websocket.onclose = (event) => {
		if (event.code === 1006) {
			provider.emit('error', ['Lost Server Connection', event, provider]);
			provider.emit('status', [{ status: 'error', error: 'Lost Server Connection' }]);
		}
		provider.ws = null;
		provider.connecting = false;
		if (provider.connected) {
			provider.connected = false;

			provider.emit('status', [
				{
					status: 'disconnected',
				},
			]);
		} else {
			provider.unsuccessfulReconnects++;
		}
		// Start with no reconnect timeout and increase timeout by
		// using exponential backoff starting with 100ms
		setTimeout(
			setupWS,
			math.min(math.pow(2, provider.unsuccessfulReconnects) * 100, provider.maxBackoffTime),
			provider
		);
	};

	websocket.onopen = () => {
		provider.lastMessageReceived = time.getUnixTime();
		provider.connecting = false;
		provider.connected = true;
		provider.unsuccessfulReconnects = 0;

		provider.onopen();

		provider.emit('status', [
			{
				status: 'connected',
			},
		]);
	};

	provider.emit('status', [
		{
			status: 'connecting',
		},
	]);
};

export default class WebsocketProvider extends Observable {
	constructor({ connect = true, maxBackoffTime = 2500 } = {}) {
		super();

		this.url = getHostUrl();
		this.connected = false;
		this.connecting = false;
		this.unsuccessfulReconnects = 0;
		this.ws = null;
		this.shouldConnect = connect;
		this.maxBackoffTime = maxBackoffTime;
		this.lastMessageReceived = 0;

		if (connect) {
			this.connect();
		}
	}

	async send(room, data) {
		if (!this.connected) {
			await this.connect();
		}
		const msgStr = JSON.stringify({ messageType: messageTypeServer, room, data });
		this.ws.send(msgStr);
	}

	async broadcast(room, data, event = undefined) {
		if (!this.connected) {
			await this.connect();
		}
		const msgStr = JSON.stringify({ messageType: messageTypeBroadcast, room, data, event });
		this.ws.send(msgStr);
	}

	destroy() {
		super.destroy();
		this.disconnect();
	}

	disconnect() {
		this.shouldConnect = false;
		if (this.ws !== null) {
			this.ws.close();
		}
	}

	connect() {
		return new Promise((resolve) => {
			this.shouldConnect = true;
			const statusListener = ({ status }) => {
				if (status === 'connected') {
					this.off('status', statusListener);
					resolve();
				}
			};
			if (!this.connected && this.ws === null) {
				setupWS(this);
				this.on('status', statusListener);
			} else if (!this.connected && this.connecting) {
				this.on('status', statusListener);
			}
		});
	}

	async subscribe(roomname, callback) {
		if (!this.connected) {
			await this.connect();
		}
		const msgStr = JSON.stringify({ messageType: messageTypeMaintenance, event: subscribeEvent, room: roomname });
		this.ws.send(msgStr);
		this.on(roomname, callback);
	}

	unsubscribe(roomname, callback) {
		if (this.ws && this.connected) {
			const msgStr = JSON.stringify({ messageType: messageTypeMaintenance, event: unsubscribeEvent, room: roomname });
			this.ws.send(msgStr);
		}
		this.off(roomname, callback);
	}

	// resubscribe to all rooms on reconnect
	onopen() {
		for (const key of this._observers.keys()) {
			// we basically say that error or status cant be subscribed to since these are events of this class
			if (!['error', 'status'].includes(key)) {
				const msgStr = JSON.stringify({ messageType: messageTypeMaintenance, event: subscribeEvent, room: key });
				this.ws.send(msgStr);
			}
		}
	}
}
